import { useMemo, useState } from "react";
import {
  VISUAL_DB_HEADERS,
  DEFAULT_GROUPS,
  DEFAULT_EXPORT_CONFIG,
  addIdToCsvList,
  removeIdFromCsvList,
  splitList,
  uniqueList,
  parseCSV,
  csvRowsToVisualRows,
  createEmptyVisualRow,
  normalizeVisualRow,
  mirrorFieldForRows,
  mirrorAllRelationFields,
  validateVisualRows,
  buildCSV,
  buildPackJson,
  downloadTextFile,
  getNextIndex,
} from "../../utils/visualCsvJsonUtils";

function RelationPicker({ label, value, currentId, availableIds, onChange }) {
  const selectedIds = splitList(value);

  const selectableIds = availableIds.filter(
    (id) => id && id !== currentId && !selectedIds.includes(id)
  );

  function handleSelect(event) {
    const idToAdd = event.target.value;

    if (!idToAdd) return;

    onChange(addIdToCsvList(value, idToAdd));
    event.target.value = "";
  }

  return (
    <label className="relation-picker">
      {label}

      <div className="relation-picker__chips">
        {selectedIds.length === 0 ? (
          <span className="relation-picker__empty">Sin relaciones</span>
        ) : (
          selectedIds.map((id) => (
            <button
              key={id}
              type="button"
              className="relation-chip"
              onClick={() => onChange(removeIdFromCsvList(value, id))}
            >
              {id} ×
            </button>
          ))
        )}
      </div>

      <select onChange={handleSelect} defaultValue="">
        <option value="">Añadir ID...</option>

        {selectableIds.map((id) => (
          <option key={id} value={id}>
            {id}
          </option>
        ))}
      </select>

      {/*<input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Manual: A_Mayus,B_Mayus"
      />*/}
    </label>
  );
}

function PairCreator({ onCreatePair }) {
  const [baseName, setBaseName] = useState("");
  const [index, setIndex] = useState("");

  function handleCreate() {
    const cleanName = baseName.trim();
    const cleanIndex = Number.parseInt(index, 10);

    if (!cleanName) {
      alert("Escribe un nombre base. Ej: A");
      return;
    }

    if (!cleanIndex || cleanIndex < 1) {
      alert("Escribe un index válido.");
      return;
    }

    onCreatePair(cleanName, cleanIndex);

    setBaseName("");
    setIndex("");
  }

  return (
    <div className="tools-letter-pair-card">
      <div className="form-block-title">
        <h3>Crear par</h3>
        <p>Crea automáticamente Base_Mayus y Base_Minus.</p>
      </div>

      <div className="tools-letter-pair-row">
        <input
          type="text"
          placeholder="Base. Ej: A, Ñ, 1"
          value={baseName}
          onChange={(e) => setBaseName(e.target.value)}
        />

        <input
          type="number"
          min="1"
          placeholder="Index"
          value={index}
          onChange={(e) => setIndex(e.target.value)}
        />

        <button
          type="button"
          className="secondary-button"
          onClick={handleCreate}
        >
          Crear par
        </button>
      </div>
    </div>
  );
}

export default function VisualCsvJsonTool() {
  const [config, setConfig] = useState(DEFAULT_EXPORT_CONFIG);
  const [groups, setGroups] = useState(DEFAULT_GROUPS);
  const [newGroupName, setNewGroupName] = useState("");

  const [rows, setRows] = useState(() => [
    createEmptyVisualRow({
      id: "A_Mayus",
      index: 1,
      group: "Mayus",
      equivalents: "A_Minus",
      verySimilar: "V_Mayus",
      similar: "Y_Mayus",
      enabled: true,
    }),
    createEmptyVisualRow({
      id: "A_Minus",
      index: 1,
      group: "Minus",
      equivalents: "A_Mayus",
      verySimilar: "V_Minus",
      similar: "Y_Minus",
      enabled: true,
    }),
  ]);

  const [newRow, setNewRow] = useState(() => createEmptyVisualRow());
  const [searchText, setSearchText] = useState("");
  const [jsonText, setJsonText] = useState("");
  const [error, setError] = useState("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const availableIds = useMemo(
    () =>
      rows
        .map((row) => row.id)
        .filter(Boolean)
        .sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  const availableGroups = useMemo(() => {
    const usedGroups = rows.map((row) => row.group).filter(Boolean);
    return uniqueList([...groups, ...usedGroups]);
  }, [groups, rows]);

  const filteredRows = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    if (!normalizedSearch) return rows;

    return rows.filter((row) =>
      VISUAL_DB_HEADERS.some((header) =>
        String(row[header] || "").toLowerCase().includes(normalizedSearch)
      )
    );
  }, [rows, searchText]);

  const stats = useMemo(() => {
    const enabledRows = rows.filter((row) => row.enabled);
    const groupsSet = new Set(rows.map((row) => row.group).filter(Boolean));

    return {
      rows: rows.length,
      enabled: enabledRows.length,
      disabled: rows.length - enabledRows.length,
      groups: groupsSet.size,
    };
  }, [rows]);

  const validationErrors = useMemo(() => validateVisualRows(rows), [rows]);

  function markChanged() {
    setHasUnsavedChanges(true);
    setJsonText("");
  }

  function updateConfig(field, value) {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));

    markChanged();
  }

  function updateRow(rowId, field, value) {
    setRows((prevRows) =>
      prevRows.map((row) =>
        row._rowId === rowId
          ? normalizeVisualRow({
              ...row,
              [field]: value,
            })
          : row
      )
    );

    markChanged();
  }

  function updateNewRow(field, value) {
    setNewRow((prev) =>
      normalizeVisualRow({
        ...prev,
        [field]: value,
      })
    );
  }

  function addGroup() {
    const cleanGroup = newGroupName.trim();

    if (!cleanGroup) {
      alert("Escribe el nombre del grupo.");
      return;
    }

    const exists = availableGroups.some(
      (group) => group.toLowerCase() === cleanGroup.toLowerCase()
    );

    if (exists) {
      alert("Ese grupo ya existe.");
      return;
    }

    setGroups((prev) => [...prev, cleanGroup]);
    setNewGroupName("");
  }

  function renameGroup(oldGroup) {
    const nextGroup = window.prompt("Nuevo nombre:", oldGroup);

    if (nextGroup === null) return;

    const cleanGroup = nextGroup.trim();

    if (!cleanGroup) {
      alert("El grupo no puede estar vacío.");
      return;
    }

    setGroups((prev) =>
      prev.map((group) => (group === oldGroup ? cleanGroup : group))
    );

    setRows((prevRows) =>
      prevRows.map((row) =>
        row.group === oldGroup ? { ...row, group: cleanGroup } : row
      )
    );

    markChanged();
  }

  function deleteGroup(groupToDelete) {
    const isUsed = rows.some((row) => row.group === groupToDelete);

    if (isUsed) {
      alert("No puedes eliminar un grupo en uso.");
      return;
    }

    setGroups((prev) => prev.filter((group) => group !== groupToDelete));
  }

  function addRow() {
    const normalized = normalizeVisualRow(newRow);

    if (!normalized.id) {
      alert("Ingresa un id.");
      return;
    }

    const exists = rows.some(
      (row) => row.id.toLowerCase() === normalized.id.toLowerCase()
    );

    if (exists) {
      alert("Ese id ya existe.");
      return;
    }

    const nextRows = [...rows, normalized];

    setRows(nextRows);
    setNewRow(
      createEmptyVisualRow({
        index: getNextIndex(nextRows),
        group: availableGroups[0] || "",
      })
    );

    markChanged();
  }

  function duplicateRow(row) {
    const newId = window.prompt("ID para la copia:", `${row.id}_copy`);

    if (newId === null) return;

    const cleanId = newId.trim();

    if (!cleanId) {
      alert("El ID no puede estar vacío.");
      return;
    }

    const exists = rows.some(
      (item) => item.id.toLowerCase() === cleanId.toLowerCase()
    );

    if (exists) {
      alert("Ya existe una fila con ese ID.");
      return;
    }

    setRows((prevRows) => [
      ...prevRows,
      createEmptyVisualRow({
        ...row,
        id: cleanId,
      }),
    ]);

    markChanged();
  }

  function deleteRow(rowId) {
    const row = rows.find((item) => item._rowId === rowId);

    const confirmed = window.confirm(
      `¿Seguro que quieres eliminar "${row?.id || ""}"?`
    );

    if (!confirmed) return;

    setRows((prevRows) => prevRows.filter((row) => row._rowId !== rowId));
    markChanged();
  }

  function moveRow(rowId, direction) {
    setRows((prevRows) => {
      const currentIndex = prevRows.findIndex((row) => row._rowId === rowId);

      if (currentIndex === -1) return prevRows;

      const nextIndex = currentIndex + direction;

      if (nextIndex < 0 || nextIndex >= prevRows.length) return prevRows;

      const copy = [...prevRows];
      const [removed] = copy.splice(currentIndex, 1);
      copy.splice(nextIndex, 0, removed);

      return copy;
    });

    markChanged();
  }

  function createPair(baseName, index) {
    const mayusId = `${baseName}_Mayus`;
    const minusId = `${baseName}_Minus`;

    const exists = rows.some(
      (row) =>
        row.id.toLowerCase() === mayusId.toLowerCase() ||
        row.id.toLowerCase() === minusId.toLowerCase()
    );

    if (exists) {
      alert("Ya existe un par con ese nombre.");
      return;
    }

    setRows((prevRows) => [
      ...prevRows,
      createEmptyVisualRow({
        id: mayusId,
        index,
        group: "Mayus",
        equivalents: minusId,
        enabled: true,
      }),
      createEmptyVisualRow({
        id: minusId,
        index,
        group: "Minus",
        equivalents: mayusId,
        enabled: true,
      }),
    ]);

    markChanged();
  }

  function mirrorField(fieldName) {
    setRows((prevRows) => mirrorFieldForRows(prevRows, fieldName));
    markChanged();
  }

  function mirrorAllFields() {
    setRows((prevRows) => mirrorAllRelationFields(prevRows));
    markChanged();
  }

  function clearRelations(fieldName) {
    const confirmed = window.confirm(
      `¿Seguro que quieres limpiar ${fieldName}?`
    );

    if (!confirmed) return;

    setRows((prevRows) =>
      prevRows.map((row) => ({
        ...row,
        [fieldName]: "",
      }))
    );

    markChanged();
  }

  async function importCsvFile(event) {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      setError("");

      const text = await file.text();
      const parsedRows = parseCSV(text);
      const importedRows = csvRowsToVisualRows(parsedRows);

      if (!importedRows.length) {
        alert("El CSV no tiene filas válidas.");
        return;
      }

      setRows(importedRows);

      setGroups((prevGroups) =>
        uniqueList([
          ...prevGroups,
          ...importedRows.map((row) => row.group).filter(Boolean),
        ])
      );

      setConfig((prev) => ({
        ...prev,
        outputCsvFileName: file.name || prev.outputCsvFileName,
      }));

      setHasUnsavedChanges(false);
      setJsonText("");
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudo importar el CSV.");
    } finally {
      event.target.value = "";
    }
  }

  function downloadCsv() {
    const errors = validateVisualRows(rows);

    if (errors.length > 0) {
      alert(errors[0]);
      return;
    }

    const csvText = buildCSV(VISUAL_DB_HEADERS, rows);

    downloadTextFile(
      config.outputCsvFileName || "visual_db.csv",
      csvText,
      "text/csv"
    );

    setHasUnsavedChanges(false);
  }

  function generateJson() {
    try {
      setError("");

      const pack = buildPackJson(rows, config);
      setJsonText(JSON.stringify(pack, null, 2));
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudo generar el JSON.");
    }
  }

  function downloadJson() {
    try {
      const output = jsonText || JSON.stringify(buildPackJson(rows, config), null, 2);

      setJsonText(output);

      downloadTextFile(
        config.outputJsonFileName || "Pack.json",
        output,
        "application/json"
      );
    } catch (err) {
      console.error(err);
      setError(err.message || "No se pudo descargar el JSON.");
    }
  }

  function copyJson() {
    if (!jsonText) {
      alert("Primero genera el JSON.");
      return;
    }

    navigator.clipboard
      .writeText(jsonText)
      .then(() => alert("JSON copiado."))
      .catch(() => alert("No se pudo copiar."));
  }

  function clearAllRows() {
    const confirmed = window.confirm("¿Eliminar todas las filas?");

    if (!confirmed) return;

    setRows([]);
    setJsonText("");
    markChanged();
  }

  return (
    <div className="tool-workspace tool-workspace--vertical">
      <section className="tools-panel">
        <div className="tools-panel__header">
          <div>
            <span className="section-eyebrow">Visual DB</span>
            <h3>CSV visual / JSON</h3>
            <p>
              Edita una base visual, crea grupos, selecciona relaciones y genera
              CSV o JSON.
            </p>
          </div>

          {hasUnsavedChanges && (
            <span className="status-badge status-badge--disabled">
              Cambios sin descargar
            </span>
          )}
        </div>

        <div className="tools-summary-grid tools-summary-grid--compact">
          <article className="tools-summary-card">
            <span>📄</span>
            <div>
              <strong>{stats.rows}</strong>
              <small>Filas</small>
            </div>
          </article>

          <article className="tools-summary-card">
            <span>✅</span>
            <div>
              <strong>{stats.enabled}</strong>
              <small>Enabled</small>
            </div>
          </article>

          <article className="tools-summary-card">
            <span>⏸️</span>
            <div>
              <strong>{stats.disabled}</strong>
              <small>Disabled</small>
            </div>
          </article>

          <article className="tools-summary-card">
            <span>🧩</span>
            <div>
              <strong>{stats.groups}</strong>
              <small>Groups</small>
            </div>
          </article>
        </div>

        {error && <p className="dashboard-error">{error}</p>}

        {validationErrors.length > 0 && (
          <div className="tools-warnings">
            <strong>Validación</strong>
            <ul>
              {validationErrors.slice(0, 5).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="tools-action-bar">
          <label className="secondary-button tools-upload-button">
            📤 Importar CSV
            <input
              type="file"
              accept=".csv,text/csv,text/plain"
              onChange={importCsvFile}
            />
          </label>

          <button
            type="button"
            className="secondary-button"
            onClick={downloadCsv}
            disabled={rows.length === 0}
          >
            Descargar CSV
          </button>

          <button
            type="button"
            className="primary-action-button"
            onClick={generateJson}
            disabled={rows.length === 0}
          >
            Generar JSON
          </button>

          <button
            type="button"
            className="secondary-button"
            onClick={clearAllRows}
            disabled={rows.length === 0}
          >
            Limpiar
          </button>
        </div>

        <div className="tools-helper-grid">
          <PairCreator onCreatePair={createPair} />

          <div className="tools-letter-pair-card">
            <div className="form-block-title">
              <h3>Grupos</h3>
              <p>Crea grupos personalizados para el campo group.</p>
            </div>

            <div className="tools-group-create-row">
              <input
                type="text"
                placeholder="Ej: Mayus, Minus, Numbers"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
              />

              <button
                type="button"
                className="secondary-button"
                onClick={addGroup}
              >
                Añadir grupo
              </button>
            </div>

            <div className="tools-group-list">
              {availableGroups.map((group) => (
                <span key={group} className="tools-group-pill">
                  {group}

                  <button type="button" onClick={() => renameGroup(group)}>
                    ✎
                  </button>

                  <button type="button" onClick={() => deleteGroup(group)}>
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="tools-mirror-card">
          <div className="form-block-title">
            <h3>Mirror de relaciones</h3>
            <p>
              Si A tiene B en una relación, Mirror agrega A en la relación de B.
            </p>
          </div>

          <div className="tools-mirror-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={() => mirrorField("equivalents")}
            >
              Mirror equivalents
            </button>

            <button
              type="button"
              className="secondary-button"
              onClick={() => mirrorField("verySimilar")}
            >
              Mirror verySimilar
            </button>

            <button
              type="button"
              className="secondary-button"
              onClick={() => mirrorField("similar")}
            >
              Mirror similar
            </button>

            <button
              type="button"
              className="primary-action-button"
              onClick={mirrorAllFields}
            >
              Mirror todo
            </button>
          </div>

          <div className="tools-mirror-actions tools-mirror-actions--danger">
            <button
              type="button"
              className="small-button small-button--danger"
              onClick={() => clearRelations("equivalents")}
            >
              Limpiar equivalents
            </button>

            <button
              type="button"
              className="small-button small-button--danger"
              onClick={() => clearRelations("verySimilar")}
            >
              Limpiar verySimilar
            </button>

            <button
              type="button"
              className="small-button small-button--danger"
              onClick={() => clearRelations("similar")}
            >
              Limpiar similar
            </button>
          </div>
        </div>

        <div className="tools-config-card">
          <div className="form-block-title">
            <h3>Configuración JSON</h3>
            <p>Estos valores se usan al generar el JSON.</p>
          </div>

          <div className="tools-form-grid">
            <label>
              packId
              <input
                type="text"
                value={config.packId}
                onChange={(e) => updateConfig("packId", e.target.value)}
              />
            </label>

            <label>
              version
              <input
                type="text"
                value={config.version}
                onChange={(e) => updateConfig("version", e.target.value)}
              />
            </label>

            <label>
              sheetFilePattern
              <input
                type="text"
                value={config.sheetFilePattern}
                onChange={(e) =>
                  updateConfig("sheetFilePattern", e.target.value)
                }
              />
              <small>Usa {"{packId}"} y {"{group}"}.</small>
            </label>

            <label>
              output JSON
              <input
                type="text"
                value={config.outputJsonFileName}
                onChange={(e) =>
                  updateConfig("outputJsonFileName", e.target.value)
                }
              />
            </label>

            <label>
              output CSV
              <input
                type="text"
                value={config.outputCsvFileName}
                onChange={(e) =>
                  updateConfig("outputCsvFileName", e.target.value)
                }
              />
            </label>

            <label>
              origin
              <select
                value={config.origin}
                onChange={(e) => updateConfig("origin", e.target.value)}
              >
                <option value="top-left">top-left</option>
                <option value="bottom-left">bottom-left</option>
              </select>
            </label>

            <label>
              cellW
              <input
                type="number"
                min="1"
                value={config.cellW}
                onChange={(e) => updateConfig("cellW", e.target.value)}
              />
            </label>

            <label>
              cellH
              <input
                type="number"
                min="1"
                value={config.cellH}
                onChange={(e) => updateConfig("cellH", e.target.value)}
              />
            </label>

            <label>
              cols
              <input
                type="number"
                min="1"
                value={config.cols}
                onChange={(e) => updateConfig("cols", e.target.value)}
              />
            </label>

            <label>
              pixelsPerUnit
              <input
                type="number"
                min="1"
                value={config.pixelsPerUnit}
                onChange={(e) =>
                  updateConfig("pixelsPerUnit", e.target.value)
                }
              />
            </label>

            <label>
              pivotX
              <input
                type="number"
                step="0.01"
                value={config.pivotX}
                onChange={(e) => updateConfig("pivotX", e.target.value)}
              />
            </label>

            <label>
              pivotY
              <input
                type="number"
                step="0.01"
                value={config.pivotY}
                onChange={(e) => updateConfig("pivotY", e.target.value)}
              />
            </label>
          </div>

          <div className="tools-options">
            <label className="switch-row">
              <input
                type="checkbox"
                checked={config.csvIndexIs1Based}
                onChange={(e) =>
                  updateConfig("csvIndexIs1Based", e.target.checked)
                }
              />
              <span className="switch-ui"></span>
              <strong>CSV index es 1-based</strong>
            </label>
          </div>
        </div>

        <div className="users-toolbar">
          <div className="search-box users-search-box">
            <span>🔎</span>

            <input
              type="text"
              placeholder="Buscar por id, group o relaciones..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          {searchText && (
            <button
              type="button"
              className="secondary-button"
              onClick={() => setSearchText("")}
            >
              Limpiar
            </button>
          )}
        </div>

        <div className="letter-db-list">
          {filteredRows.map((row) => {
            const realIndex = rows.findIndex(
              (item) => item._rowId === row._rowId
            );

            return (
              <article className="letter-db-row-card" key={row._rowId}>
                <div className="letter-db-row-card__header">
                  <div>
                    <span className="localization-row-card__index">
                      #{realIndex + 1}
                    </span>

                    <strong>{row.id || "Sin id"}</strong>

                    <span
                      className={
                        row.enabled
                          ? "status-badge status-badge--enabled"
                          : "status-badge status-badge--disabled"
                      }
                    >
                      {row.enabled ? "TRUE" : "FALSE"}
                    </span>
                  </div>

                  <div className="table-actions">
                    <button
                      type="button"
                      className="small-button"
                      onClick={() => moveRow(row._rowId, -1)}
                      disabled={realIndex <= 0}
                    >
                      ↑
                    </button>

                    <button
                      type="button"
                      className="small-button"
                      onClick={() => moveRow(row._rowId, 1)}
                      disabled={realIndex >= rows.length - 1}
                    >
                      ↓
                    </button>

                    <button
                      type="button"
                      className="small-button"
                      onClick={() => duplicateRow(row)}
                    >
                      Duplicar
                    </button>

                    <button
                      type="button"
                      className="small-button small-button--danger"
                      onClick={() => deleteRow(row._rowId)}
                    >
                      Eliminar
                    </button>
                  </div>
                </div>

                <div className="letter-db-fields">
                  <label>
                    id
                    <input
                      type="text"
                      value={row.id}
                      onChange={(e) =>
                        updateRow(row._rowId, "id", e.target.value)
                      }
                    />
                  </label>

                  <label>
                    index
                    <input
                      type="number"
                      min="1"
                      value={row.index}
                      onChange={(e) =>
                        updateRow(row._rowId, "index", e.target.value)
                      }
                    />
                  </label>

                  <label>
                    group
                    <select
                      value={row.group}
                      onChange={(e) =>
                        updateRow(row._rowId, "group", e.target.value)
                      }
                    >
                      <option value="">Seleccionar...</option>

                      {availableGroups.map((group) => (
                        <option key={group} value={group}>
                          {group}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label>
                    enabled
                    <select
                      value={row.enabled ? "TRUE" : "FALSE"}
                      onChange={(e) =>
                        updateRow(
                          row._rowId,
                          "enabled",
                          e.target.value === "TRUE"
                        )
                      }
                    >
                      <option value="TRUE">TRUE</option>
                      <option value="FALSE">FALSE</option>
                    </select>
                  </label>

                  <RelationPicker
                    label="equivalents"
                    value={row.equivalents}
                    currentId={row.id}
                    availableIds={availableIds}
                    onChange={(value) =>
                      updateRow(row._rowId, "equivalents", value)
                    }
                  />

                  <RelationPicker
                    label="verySimilar"
                    value={row.verySimilar}
                    currentId={row.id}
                    availableIds={availableIds}
                    onChange={(value) =>
                      updateRow(row._rowId, "verySimilar", value)
                    }
                  />

                  <RelationPicker
                    label="similar"
                    value={row.similar}
                    currentId={row.id}
                    availableIds={availableIds}
                    onChange={(value) =>
                      updateRow(row._rowId, "similar", value)
                    }
                  />
                </div>
              </article>
            );
          })}

          {filteredRows.length === 0 && (
            <p className="empty-state">No hay filas para mostrar.</p>
          )}
        </div>

        <div className="add-localization-card">
          <div className="form-block-title">
            <h3>Añadir fila manual</h3>
            <p>Se agregará al final del CSV.</p>
          </div>

          <div className="letter-db-fields">
            <label>
              id
              <input
                type="text"
                value={newRow.id}
                onChange={(e) => updateNewRow("id", e.target.value)}
                placeholder="A_Mayus"
              />
            </label>

            <label>
              index
              <input
                type="number"
                min="1"
                value={newRow.index}
                onChange={(e) => updateNewRow("index", e.target.value)}
              />
            </label>

            <label>
              group
              <select
                value={newRow.group}
                onChange={(e) => updateNewRow("group", e.target.value)}
              >
                <option value="">Seleccionar...</option>

                {availableGroups.map((group) => (
                  <option key={group} value={group}>
                    {group}
                  </option>
                ))}
              </select>
            </label>

            <label>
              enabled
              <select
                value={newRow.enabled ? "TRUE" : "FALSE"}
                onChange={(e) =>
                  updateNewRow("enabled", e.target.value === "TRUE")
                }
              >
                <option value="TRUE">TRUE</option>
                <option value="FALSE">FALSE</option>
              </select>
            </label>

            <RelationPicker
              label="equivalents"
              value={newRow.equivalents}
              currentId={newRow.id}
              availableIds={availableIds}
              onChange={(value) => updateNewRow("equivalents", value)}
            />

            <RelationPicker
              label="verySimilar"
              value={newRow.verySimilar}
              currentId={newRow.id}
              availableIds={availableIds}
              onChange={(value) => updateNewRow("verySimilar", value)}
            />

            <RelationPicker
              label="similar"
              value={newRow.similar}
              currentId={newRow.id}
              availableIds={availableIds}
              onChange={(value) => updateNewRow("similar", value)}
            />
          </div>

          <div className="edit-modal__actions">
            <button
              type="button"
              className="primary-action-button"
              onClick={addRow}
            >
              Añadir fila
            </button>
          </div>
        </div>
      </section>

      <section className="tools-panel tools-output-panel">
        <div className="tools-panel__header">
          <div>
            <span className="section-eyebrow">Output</span>
            <h3>JSON generado</h3>
            <p>El CSV conserva la estructura visual editable.</p>
          </div>

          <div className="tools-output-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={copyJson}
              disabled={!jsonText}
            >
              Copiar
            </button>

            <button
              type="button"
              className="primary-action-button"
              onClick={downloadJson}
              disabled={rows.length === 0}
            >
              Descargar JSON
            </button>
          </div>
        </div>

        <pre className="tools-json-preview tools-json-preview--wide">
          {jsonText || "Genera el JSON para verlo aquí."}
        </pre>
      </section>
    </div>
  );
}